import { Lock } from "lucide-react";
import type { GridlineShape } from "sketch/shapes/gridline";
import {
  DefaultToolbar,
  DefaultToolbarContent,
  TldrawUiMenuItem,
  useEditor,
  useIsToolSelected,
  useTools,
  useValue,
  type DefaultToolbarProps,
  type TLUiIconJsx,
} from "tldraw";

// Not `tlui-icon`: that class masks a background color, blanking JSX icons.
const LockIcon: TLUiIconJsx = (
  <div style={{ width: 18, height: 18 }}>
    <Lock className="h-full w-full" strokeWidth={1.75} />
  </div>
);

/**
 * Lock every gridline on the page (and unlock them again): locked gridlines
 * can't be dragged by a stray gesture, but dims still bind to them and
 * direct-drive still moves them, so the grid stays authoritative.
 */
function GridlineLockItem() {
  const editor = useEditor();
  const gridlines = useValue(
    "gridlines",
    () =>
      editor
        .getCurrentPageShapes()
        .filter((s): s is GridlineShape => s.type === "gridline"),
    [editor],
  );
  const locked = gridlines.length > 0 && gridlines.every((s) => s.isLocked);
  if (!gridlines.length) return null;
  return (
    <TldrawUiMenuItem
      id="gridline-lock"
      label={locked ? "Unlock gridlines" : "Lock gridlines"}
      icon={LockIcon}
      isSelected={locked}
      onSelect={() => {
        editor.updateShapes(
          gridlines.map((s) => ({
            id: s.id,
            type: s.type,
            isLocked: !locked,
          })),
        );
      }}
    />
  );
}

/**
 * The default markup toolbar plus the drafting register: gridline, dim and
 * callout. (More drafting tools land here as they arrive.) The sticky note
 * is gone from the default content: calloutToolOverride drops it.
 */
export function SketchToolbar(props: DefaultToolbarProps) {
  const tools = useTools();
  const gridline = tools.gridline;
  const dim = tools.dim;
  const callout = tools.callout;
  const gridlineSelected = useIsToolSelected(gridline);
  const dimSelected = useIsToolSelected(dim);
  const calloutSelected = useIsToolSelected(callout);
  return (
    <DefaultToolbar {...props}>
      {gridline && (
        <TldrawUiMenuItem {...gridline} isSelected={gridlineSelected} />
      )}
      {dim && <TldrawUiMenuItem {...dim} isSelected={dimSelected} />}
      {callout && (
        <TldrawUiMenuItem {...callout} isSelected={calloutSelected} />
      )}
      <GridlineLockItem />
      <DefaultToolbarContent />
    </DefaultToolbar>
  );
}
